import type { Fill, Font, Worksheet } from "exceljs";
import { DateTime } from "luxon";
import type { ReportFieldDefinition, ReportRow } from "~/utils/types/assistant-report-artifact";
import { isCalendarDayValue, isMonthBucketValue } from "~/utils/types/assistant-report-artifact";

import type { ReportExportBlock } from "./AssistantResults";
import { assistantMoneyDecimals, instantExportHeader } from "./report-field-formatters";

export const XLSX_MIME_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

// Copied from the Notes export (src/server/domain/notes/services/notes-export-excel.service.ts); server code cannot load in the browser.
const HEADER_FILL: Fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E79" } };
const HEADER_FONT: Partial<Font> = { bold: true, color: { argb: "FFFFFFFF" }, size: 11 };

const PIXELS_PER_CHARACTER = 7;
const MAX_SHEET_NAME_LENGTH = 31;

export type ExcelCell = { value: string | number | Date | null; numFmt?: string };

const EMPTY: ExcelCell = { value: null };

// One formatter per currency, not per money cell: a large report writes tens of thousands of cells.
const currencySymbols = new Map<string, string | null>();

/** The symbol the screen shows for a currency ("$", "CA$"), or none when the code is not a currency Intl knows. */
const currencySymbol = (code: string): string | null => {
  const key = code.trim().toUpperCase();
  const cached = currencySymbols.get(key);
  if (cached !== undefined) {
    return cached;
  }
  let symbol: string | null;
  try {
    const parts = new Intl.NumberFormat(undefined, { style: "currency", currency: key }).formatToParts(0);
    symbol = parts.find(part => part.type === "currency")?.value ?? null;
  } catch {
    symbol = null;
  }
  currencySymbols.set(key, symbol);
  return symbol;
};

const moneyFormat = (value: number, field: ReportFieldDefinition, currencyCode: unknown): string => {
  const digits = assistantMoneyDecimals(value, field.moneyKind) > 2 ? "#,##0.00##" : "#,##0.00";
  const symbol = typeof currencyCode === "string" ? currencySymbol(currencyCode) : null;
  return symbol ? `"${symbol}"${digits};-"${symbol}"${digits}` : digits;
};

/**
 * One Excel cell per report value, typed only from the field's declared semanticType, so every report gets a typed
 * workbook with no per-report code. A value of the wrong kind for its type writes an empty cell, as the screen shows nothing.
 */
export const excelCell = (field: ReportFieldDefinition, row: ReportRow, orgCurrencyCode: string, timeZone = "UTC"): ExcelCell => {
  const value = row[field.key];
  if (value === null || value === undefined) {
    return EMPTY;
  }
  const semanticType = field.semanticType;
  switch (semanticType) {
    case "text":
      // A string cell is never a formula, so a value starting with "=" stays text.
      return typeof value === "string" ? { value } : EMPTY;
    case "money": {
      if (typeof value !== "number" || !Number.isFinite(value)) {
        return EMPTY;
      }
      const currencyCode = field.currencyCodeKey ? row[field.currencyCodeKey] : orgCurrencyCode;
      return { value, numFmt: moneyFormat(value, field, currencyCode) };
    }
    case "percentage":
      return typeof value === "number" && Number.isFinite(value) ? { value: value / 100, numFmt: "0.00%" } : EMPTY;
    case "number":
      return typeof value === "number" && Number.isFinite(value)
        ? { value, numFmt: Number.isInteger(value) ? "#,##0" : "#,##0.00" }
        : EMPTY;
    case "decimalString": {
      const parsed = typeof value === "string" ? Number(value) : Number.NaN;
      return Number.isFinite(parsed) ? { value: parsed, numFmt: "0.00##" } : EMPTY;
    }
    case "calendarDay": {
      if (typeof value !== "string" || !isCalendarDayValue(value)) {
        return EMPTY;
      }
      const [year, month, day] = value.split("-").map(Number);
      return { value: new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, day ?? 1)), numFmt: "mmm d, yyyy" };
    }
    case "monthBucket": {
      if (typeof value !== "string" || !isMonthBucketValue(value)) {
        return EMPTY;
      }
      const [year, month] = value.split("-").map(Number);
      return { value: new Date(Date.UTC(year ?? 0, (month ?? 1) - 1, 1)), numFmt: "mmm yyyy" };
    }
    case "instant": {
      // Excel has no time zones: the cell holds the org wall time, stamped as UTC as the statement export does.
      const epoch = typeof value === "string" ? Date.parse(value) : Number.NaN;
      if (!Number.isFinite(epoch)) {
        return EMPTY;
      }
      const wallTime = DateTime.fromJSDate(new Date(epoch)).setZone(timeZone).setZone("utc", { keepLocalTime: true }).toJSDate();
      return { value: wallTime, numFmt: "mmm d, yyyy h:mm AM/PM" };
    }
    case "boolean":
      return typeof value === "boolean" ? { value: value ? "Yes" : "No" } : EMPTY;
    default: {
      const unmapped: never = semanticType;
      throw new Error(`Unmapped report field type: ${String(unmapped)}`);
    }
  }
};

/** Excel refuses [ ] : * ? / \, an apostrophe first or last, "History", and more than 31 characters. */
export const excelSheetName = (title: string): string => {
  const name = title
    .replace(/[[\]:*?/\\]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, MAX_SHEET_NAME_LENGTH)
    .replace(/^'+|'+$/g, "")
    .trim();
  return name && name.toLowerCase() !== "history" ? name : "Report";
};

const writeCell = (sheet: Worksheet, rowNumber: number, columnNumber: number, cell: ExcelCell): void => {
  const target = sheet.getCell(rowNumber, columnNumber);
  target.value = cell.value;
  if (cell.numFmt) {
    target.numFmt = cell.numFmt;
  }
};

const writeHeaderCell = (sheet: Worksheet, rowNumber: number, columnNumber: number, label: string): void => {
  const target = sheet.getCell(rowNumber, columnNumber);
  target.value = label;
  target.fill = HEADER_FILL;
  target.font = HEADER_FONT;
};

export const buildReportWorkbook = async (
  blocks: readonly ReportExportBlock[],
  options: { sheetName: string; orgCurrencyCode: string; timeZone: string; columnWidthPx: (field: ReportFieldDefinition) => number },
): Promise<ArrayBuffer> => {
  // Loaded on the first Excel export only, never with the page.
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(excelSheetName(options.sheetName));
  const widths: number[] = [];
  const widen = (columnIndex: number, characters: number) => {
    widths[columnIndex] = Math.max(widths[columnIndex] ?? 0, Math.ceil(characters));
  };

  const headerLabel = (field: ReportFieldDefinition): string =>
    field.semanticType === "instant" ? instantExportHeader(field.label, options.timeZone) : field.label;

  let rowNumber = 1;
  blocks.forEach((block, blockIndex) => {
    if (blockIndex > 0) {
      rowNumber += 1;
    }
    if (block.kind === "pairs") {
      for (const field of block.fields) {
        const label = headerLabel(field);
        writeHeaderCell(sheet, rowNumber, 1, label);
        writeCell(sheet, rowNumber, 2, excelCell(field, block.row, options.orgCurrencyCode, options.timeZone));
        widen(0, label.length + 2);
        widen(1, options.columnWidthPx(field) / PIXELS_PER_CHARACTER);
        rowNumber += 1;
      }
      return;
    }
    const headerRowNumber = rowNumber;
    block.fields.forEach((field, columnIndex) => {
      writeHeaderCell(sheet, headerRowNumber, columnIndex + 1, headerLabel(field));
      widen(columnIndex, options.columnWidthPx(field) / PIXELS_PER_CHARACTER);
    });
    if (block.fields.length > 0) {
      sheet.autoFilter = { from: { row: headerRowNumber, column: 1 }, to: { row: headerRowNumber, column: block.fields.length } };
    }
    if (blockIndex === 0) {
      sheet.views = [{ state: "frozen", ySplit: headerRowNumber }];
    }
    for (const row of block.rows) {
      rowNumber += 1;
      block.fields.forEach((field, columnIndex) =>
        writeCell(sheet, rowNumber, columnIndex + 1, excelCell(field, row, options.orgCurrencyCode, options.timeZone)),
      );
    }
    rowNumber += 1;
  });
  widths.forEach((width, columnIndex) => {
    sheet.getColumn(columnIndex + 1).width = width;
  });

  return workbook.xlsx.writeBuffer();
};
